type AnyNode = any;

function cloneContent(content: any): any {
  if (!content || typeof content !== 'object') return content;
  return JSON.parse(JSON.stringify(content));
}

function collectText(node: AnyNode): string {
  if (!node || typeof node !== 'object') return '';
  let text = typeof node.text === 'string' ? node.text : '';
  if (Array.isArray(node.children)) {
    text += node.children.map(collectText).join('');
  }
  return text;
}

function normalizeForMatch(value: string): string {
  return value
    .replace(/\u00a0/g, ' ')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function isMostlyUppercase(value: string): boolean {
  const letters = value.replace(/[^A-Za-zÁÉÍÓÚÜÑÀÈÉÍÏÒÓÚÜÇáéíóúüñàèéíïòóúüç]/g, '');
  if (letters.length < 4) return false;
  const upper = letters.toUpperCase();
  return letters === upper;
}

function isLiturgicalHeading(rawText: string): boolean {
  const original = rawText.replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
  const text = normalizeForMatch(original);
  if (!text) return false;
  if (text.length > 140) return false;

  const exact = new Set([
    'lecturas',
    'lectures',
    'letture',
    'leituras',
    'lectiones',

    'primera lectura',
    'primera lectura',
    'first reading',
    'premiere lecture',
    'prima lettura',
    'primeira leitura',
    'lectio prima',

    'segunda lectura',
    'second reading',
    'deuxieme lecture',
    'seconda lettura',
    'segunda leitura',
    'lectio secunda',

    'tercera lectura',
    'third reading',

    'evangelio',
    'gospel',
    'evangile',
    'evangeli',
    'evangelium',
    'vangelo',

    'salmo responsorial',
    'responsorial psalm',
    'psaume responsorial',
    'salm responsorial',
    'salm responsoriale',
    'psalmus responsorius',

    'aclamacion antes del evangelio',
    'acclamation avant levangile',
    'aclamacio abans de levangeli',
    'acclamazione al vangelo',
    'aclamacao antes do evangelho',

    'aleluya',
    'alleluia',
    'al leluia',

    'oracion colecta',
    'oracio collecta',
    'oracio col lecta',
    'collecta',
    'collect prayer',
    'orazione colletta',
    'oracao coleta',

    'oracion sobre las ofrendas',
    'oracio sobre les ofrenes',
    'oracion despues de la comunion',
    'oracio despres de la comunio',

    'antifona de entrada',
    'antifona dentrada',
    'antifona de comunion',
    'antifona de comunio',

    'invitatorio',
    'invitatori',
    'invitatory',

    'invocacion inicial',
    'invocacio inicial',
    'initial invocation',

    'himno',
    'himne',
    'hymn',
    'inno',

    'salmodia',
    'salmodia',
    'psalmody',
    'psalmodie',
    'salmodia',

    'lectura breve',
    'lectura breu',
    'short reading',
    'lecture breve',
    'lettura breve',
    'leitura breve',
    'lectio brevis',

    'responsorio breve',
    'responsori breu',
    'short responsory',
    'repons bref',
    'responsorio breve',
    'responsorium breve',

    'cantico evangelico',
    'cantic evangelic',
    'canticle evangelic',
    'evangelical canticle',
    'cantique evangelique',
    'cantico evangelico',

    'preces',
    'pregaries',
    'intercessions',
    'prieres',
    'intercessioni',
    'preces',

    'padre nuestro',
    'pare nostre',
    'our father',
    'notre pere',
    'padre nostro',
    'pai nosso',
    'pater noster',

    'oracion',
    'oracio',
    'prayer',
    'priere',
    'orazione',
    'oracao',
    'oratio',

    'conclusion',
    'conclusio'
  ]);

  if (exact.has(text)) return true;

  const starts = [
    'salmo responsorial:',
    'responsorial psalm:',
    'psaume responsorial',
    'salm responsorial',
    'psalmus responsorius',

    'evangelio',
    'gospel',
    'evangile',
    'evangeli',
    'evangelium',
    'vangelo',

    'primera lectura',
    'segunda lectura',
    'first reading',
    'second reading',
    'premiere lecture',
    'deuxieme lecture',
    'prima lettura',
    'seconda lettura',
    'primeira leitura',
    'segunda leitura',

    'lectura del ',
    'lectura de la ',
    'lectura de los ',
    'lectura del libro',
    'lectura de la carta',
    'lectura del santo evangelio',
    'comienzo del santo evangelio',
    'proclamacion del santo evangelio',

    'lectura breu',
    'lectura de ',
    'lectura del sant evangeli',
    'proclamacio de levangeli',

    'aclamacion antes del evangelio',
    'aclamacio abans de levangeli',
    'acclamation avant levangile',
    'acclamazione al vangelo',
    'aclamacao antes do evangelho',

    'aleluya',
    'alleluia',

    'oracion colecta',
    'oracio collecta',
    'oracio sobre les ofrenes',
    'oracio despres de la comunio',
    'oracion sobre las ofrendas',
    'oracion despues de la comunion',

    'antifona de entrada',
    'antifona dentrada',
    'antifona de comunion',
    'antifona de comunio',
    'ant.',

    'invocacion inicial',
    'invocacio inicial',
    'invitatorio',
    'invitatori',

    'himno',
    'himne:',
    'hymn',
    'inno',

    'salmo ',
    'salm ',
    'psalm ',
    'psaume ',
    'psalmus ',

    'salmodia',
    'salmòdia',
    'psalmody',
    'psalmodie',

    'cantico ',
    'cantic ',
    'canticle ',
    'cantique ',

    'lectura breve',
    'lectura breu',
    'short reading',
    'lecture breve',
    'lettura breve',
    'leitura breve',

    'responsorio breve',
    'responsori breu',
    'short responsory',
    'repons bref',

    'preces',
    'pregaries',
    'prieres',
    'intercessions',

    'oracion',
    'oracio',
    'prayer',
    'priere',
    'orazione',
    'oracao'
  ];

  if (starts.some((prefix) => text.startsWith(normalizeForMatch(prefix)))) return true;

  if (original.length <= 45 && isMostlyUppercase(original)) return true;

  return false;
}

function isBlankParagraph(node: AnyNode): boolean {
  if (!node || node.type !== 'paragraph') return false;
  const text = collectText(node).replace(/\u00a0/g, ' ').trim();
  return text === '';
}

function makeBlankParagraph(): AnyNode {
  return {
    children: [],
    direction: null,
    format: '',
    indent: 0,
    type: 'paragraph',
    version: 1
  };
}

function makeLineBreak(): AnyNode {
  return { type: 'linebreak', version: 1 };
}

function applyBoldToTextNodes(node: AnyNode): void {
  if (!node || typeof node !== 'object') return;
  if (node.type === 'text') {
    const current = typeof node.format === 'number' ? node.format : 0;
    node.format = current | 1;
  }
  if (Array.isArray(node.children)) {
    node.children.forEach(applyBoldToTextNodes);
  }
}

function enhanceInlineLinebreakHeadings(paragraph: AnyNode): void {
  if (!paragraph || paragraph.type !== 'paragraph' || !Array.isArray(paragraph.children)) return;

  const output: AnyNode[] = [];

  paragraph.children.forEach((child: AnyNode, index: number) => {
    if (child?.type === 'text' && typeof child.text === 'string' && isLiturgicalHeading(child.text)) {
      const prev = output[output.length - 1];
      const prev2 = output[output.length - 2];

      const alreadySeparated =
        prev?.type === 'linebreak' && prev2?.type === 'linebreak';

      if (output.length > 0 && !alreadySeparated) {
        if (prev?.type !== 'linebreak') output.push(makeLineBreak());
        output.push(makeLineBreak());
      }

      applyBoldToTextNodes(child);
    }

    output.push(child);
  });

  paragraph.children = output;
}

export function enhanceLiturgicalReadingContent(content: any): any {
  const cloned = cloneContent(content);
  const children = cloned?.root?.children;

  if (!Array.isArray(children)) return cloned;

  const enhanced: AnyNode[] = [];

  children.forEach((node: AnyNode) => {
    if (node?.type === 'paragraph') {
      enhanceInlineLinebreakHeadings(node);

      const text = collectText(node);
      if (isLiturgicalHeading(text)) {
        if (enhanced.length > 0 && !isBlankParagraph(enhanced[enhanced.length - 1])) {
          enhanced.push(makeBlankParagraph());
        }
        applyBoldToTextNodes(node);
      }
    }
    enhanced.push(node);
  });

  cloned.root.children = enhanced;
  return cloned;
}
